// Libs
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import DirectionsService from '@mapbox/mapbox-sdk/services/directions';
import { FeatureCollection, Geometry, GeoJsonProperties, Position } from 'geojson';
import { MapboxProfile } from '@mapbox/mapbox-sdk/lib/classes/mapi-request';
import { MapiResponse } from '@mapbox/mapbox-sdk/lib/classes/mapi-response';

// Constants
import { MAPBOX_TOKEN } from '../../constants/tokens';
import { colors } from '../../constants/colors';

// Interfaces
import { Stop } from '../../interfaces';

// Contexts
import { useStats } from '../../contexts/stats.context';
import { useNavigation } from '../../contexts/navigation.context';

import 'mapbox-gl/dist/mapbox-gl.css';
import './map.scss';
import { useErrorBoundary } from '../../contexts/error-boundary.context';

interface MapProps {
	profile?: MapboxProfile;
	stops?: Array<Stop>;
}

interface MarkerProps {
	coordinates: [number, number];
	color: string;
}

enum Colors {
	blue = 'blue',
	red = 'red',
	orange = 'orange',
	cian = 'cian',
	grass = 'grass',
	purple = 'purple',
	olive = 'olive',
	pink = 'pink',
	lime = 'lime',
	plum = 'plum',
	sky = 'sky',
	green = 'green'
}

const Map: React.FunctionComponent<MapProps> = ({ profile = 'driving', stops = [] }) => {
	const { map: mapRef, setSteps } = useNavigation();
	const mapElRef = useRef<HTMLDivElement | null>(null);
	const markersRef = useRef<Array<mapboxgl.Marker>>([]);
	const sourcesRef = useRef<Array<string>>([]);
	const { setDistance, setStops, setTime } = useStats();
	const { setIsError } = useErrorBoundary();

	const directionsService = useMemo(
		() =>
			DirectionsService({
				accessToken: MAPBOX_TOKEN
			}),
		[]
	);

	function getGeoJson(directions: MapiResponse) {
		const coordinates: Position[] = [];

		directions.body.routes[0]?.legs?.forEach((leg: { steps: [] }) => {
			leg.steps.forEach((step: { intersections: [] }) => {
				step.intersections.forEach((intersection: { location: [number, number] }) => {
					coordinates.push(intersection.location);
				});
			});
		});

		const geojson: FeatureCollection<Geometry, GeoJsonProperties> = {
			type: 'FeatureCollection',
			features: [
				{
					type: 'Feature',
					properties: {},
					geometry: {
						type: 'LineString',
						coordinates
					}
				}
			]
		};

		return geojson;
	}

	function addSource(
		map: mapboxgl.Map,
		id: string,
		geojson: FeatureCollection<Geometry, GeoJsonProperties>,
		colors: { start: string; end: string }
	) {
		map.addSource(id, {
			type: 'geojson',
			lineMetrics: true,
			data: geojson
		});

		map.addLayer({
			type: 'line',
			source: id,
			id,
			paint: {
				'line-width': 5,
				'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, colors.start, 1, colors.end]
			},
			layout: {
				'line-cap': 'square',
				'line-join': 'round'
			}
		});

		if (sourcesRef.current) {
			sourcesRef.current.push(id);
		}
	}

	function addMarker(id: number, map: mapboxgl.Map, marker: MarkerProps) {
		const el = document.createElement('div');

		el.className = `marker marker--${marker.color}`;

		const markerRef = new mapboxgl.Marker(el).setLngLat(marker.coordinates).addTo(map);

		if (markersRef.current) {
			markersRef.current[id] = markerRef;
		}
	}

	async function setupMap() {
		mapboxgl.accessToken = MAPBOX_TOKEN;

		const map = new mapboxgl.Map({
			container: mapElRef.current!,
			style: 'mapbox://styles/mapbox/streets-v11',
			preserveDrawingBuffer: true
		});

		map.setPadding({ left: 60, right: 320, top: 60, bottom: 60 });

		if (mapRef) {
			mapRef.current = map;
		}
	}

	function clearMap() {
		if (markersRef.current) {
			markersRef.current.forEach(marker => marker.remove());
			markersRef.current = [];
		}

		if (sourcesRef.current) {
			sourcesRef.current.forEach(id => {
				mapRef?.current!.removeLayer(id);
				mapRef?.current!.removeSource(id);
			});
			sourcesRef.current = [];
		}
	}

	async function handleSteps() {
		try {
			clearMap();

			if (!profile || !stops.length) {
				setStops(0);
				return;
			}

			const markerColors = Object.values(Colors);
			let stepsInstructions: any[] = [];

			let time = 0;
			let distance = 0;

			for (let index = 0; index < stops.length - 1; index++) {
				const from = stops[index].coordinates;
				const to = stops[index + 1].coordinates;
				const startColor = colors[index % colors.length];
				const endColor =
					index === stops.length - 2 ? colors[colors.length - 1] : colors[(index + 1) % colors.length];

				const directions = await directionsService
					.getDirections({
						profile,
						waypoints: [
							{
								coordinates: from
							},
							{
								coordinates: to
							}
						],
						steps: true
					})
					.send();

				stepsInstructions = [
					...stepsInstructions,
					...directions.body?.routes?.[0]?.legs.reduce((acc: any, cur: any) => {
						return [...acc, ...cur.steps];
					}, [])
				];

				time += directions.body?.routes?.[0]?.duration ?? 0;
				distance += directions.body?.routes?.[0]?.distance ?? 0;

				const geoJson = getGeoJson(directions);

				const isSourceExists = !!mapRef?.current!.getSource(index.toString());

				if (isSourceExists) {
					mapRef?.current!.removeLayer(index.toString());
					mapRef?.current!.removeSource(index.toString());
				}

				addSource(mapRef?.current!, index.toString(), geoJson, { start: startColor, end: endColor });
				addMarker(index, mapRef?.current!, {
					coordinates: stops[index].coordinates,
					color: markerColors[index % colors.length]
				});
			}

			addMarker(stops.length - 1, mapRef?.current!, {
				coordinates: stops[stops.length - 1].coordinates,
				color: Colors.green
			});

			const bounds = new mapboxgl.LngLatBounds();

			stops.forEach(stop => {
				bounds.extend(stop.coordinates);
			});

			mapRef?.current!.fitBounds(bounds);

			setStops(stops.length);
			setTime(time);
			setDistance(distance);
			setSteps(stepsInstructions);
		} catch {
			setIsError(true);
		}
	}

	useEffect(() => {
		handleSteps();
	}, [profile, stops]);

	useLayoutEffect(() => {
		setupMap();
	}, []);

	return <div id="map" className="map" ref={mapElRef}></div>;
};

export default Map;
